import { Loader2Icon } from "lucide-react"
import type * as React from "react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import type { ProfileResponse, UserResponse, UserSignInMethod } from "@steelbill/types"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { NativeSelect, NativeSelectOption } from "@/components/ui/native-select"
import { Separator } from "@/components/ui/separator"
import { Skeleton } from "@/components/ui/skeleton"
import { useCan } from "@/features/auth/auth-context"
import {
  attributeProfileError,
  useProfile,
  useProfileWrite,
  type ProfileRefusal,
} from "@/features/preferences/use-profile"
import { api } from "@/lib/api"
import { ApiError } from "@/lib/api-error"
import { endpoints } from "@/lib/endpoints"

/**
 * MY OWN NAME, MY OWN SECRET, MY OWN SIGN-IN — inside the settings sheet the
 * owner already knows how to reach (§2, §2.1, §11.1).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * WHY IT IS IN THE SHEET AND NOT ON A SCREEN OF ITS OWN. He asked for it "in the
 * profile settings", and there is exactly one thing by that name: the slide-over
 * this file is rendered into. A second surface would mean two places to look for
 * one's own account and two designs to keep in step — and the sheet's whole
 * argument (see `ProfileSheet.tsx`) is that it leaves him where he was. Until
 * this landed the sheet carried an alert saying a PIN change was "not here yet"
 * and told him to ask his son. That alert is what this replaces.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * FIVE RULES, ALL OF THEM THE SERVER'S — THIS SCREEN ONLY DECLINES TO CONTRADICT
 * THEM:
 *
 *  1. A SECRET OR IDENTIFIER CHANGE ASKS FOR THE CURRENT SECRET IN THE SAME
 *     FORM. Not a step before it, not a dialog that can be dismissed: one
 *     submit carries both, because the re-authentication and the change are one
 *     server request (`changeOwnCredential`), and a separate "verify" step is a
 *     step somebody eventually finds a way to skip.
 *
 *  2. ONLY A DOOR THAT IS OPEN IS OFFERED. `doors` comes off the server's own
 *     row; a PIN account is shown a PIN and a phone number and NOTHING about
 *     passwords or email. Drawing a control that is certain to 403 or 409 is
 *     worse than drawing none — it teaches the reader that the app's buttons are
 *     suggestions.
 *
 *  3. A PIN NEVER REWRITES A PASSWORD. `assertProofMayRewrite` permits exactly
 *     one crossing — password speaks for PIN, never the reverse — because an
 *     account holding both must be as strong as its STRONGER secret, not its
 *     weaker one. This screen simply never offers a crossing at all: the secret
 *     you prove is always the secret you are changing, which is legal in both
 *     directions and needs no rule to be remembered here.
 *
 *  4. ROLE AND ACTIVE STATE ARE ABSENT. Not disabled — absent. They are not in
 *     any schema on this prefix (`.strict()`, no such field), the services name
 *     the one column they write, and there is nothing on this component that
 *     could send one. The identity readout above (the "Signed in" section) is
 *     pre-existing and is a READOUT; nothing here manages either fact.
 *
 *  5. NO SECRET SURVIVES THE SUBMIT. Every secret input is cleared the instant
 *     the request settles — success or refusal — and nothing carries one into a
 *     query key, a URL, a toast, a log or a cache. See the header of
 *     `use-profile.ts` for why this does not go through `useMutation`.
 */
export function AccountSettings(): React.JSX.Element | null {
  /*
   * §2.1 — the capability, never the role. `profile.manage` is held by all three
   * roles today, which is precisely why it must be ASKED rather than assumed: a
   * fourth role that does not hold it should find this section absent, not find
   * a form that 403s. Deny by default.
   */
  const mayManage = useCan("profile.manage")
  if (!mayManage) return null
  return <AccountForms />
}

function AccountForms(): React.JSX.Element {
  const { t } = useTranslation()
  const profile = useProfile()

  if (profile.isPending) return <Skeleton className="h-56 w-full" />

  if (profile.isError || profile.data === undefined) {
    return (
      <Alert variant="destructive">
        <AlertTitle>{t("err.couldNotLoad")}</AlertTitle>
        <AlertDescription className="flex flex-col items-start gap-3">
          <span>
            {profile.error instanceof ApiError && !profile.error.isServerError
              ? profile.error.message
              : t("err.loadFailed")}
          </span>
          <Button
            variant="outline"
            onClick={() => {
              void profile.refetch()
            }}
          >
            {t("btn.retry")}
          </Button>
        </AlertDescription>
      </Alert>
    )
  }

  return <AccountSections me={profile.data.data} />
}

function AccountSections({ me }: { me: UserResponse }): React.JSX.Element {
  const { t } = useTranslation()

  /*
   * `doors` IS THE SERVER'S ANSWER AND THE ONLY ONE. It is optional in the type
   * for a migration reason (`UserResponse.doors`), never absent on the wire —
   * `signInWith` is the honest fallback for an older API, and an empty array is
   * a real answer meaning this account cannot sign in at all.
   */
  const open: readonly UserSignInMethod[] = me.doors ?? [me.signInWith]

  /*
   * AND ONLY A DOOR WHOSE SECRET IS ACTUALLY SET — rule 2, applied to the state
   * an ADMIN produces by opening a door without also setting a credential
   * (`POST /users/:id/doors` writes the flag, warns, and does not refuse).
   * `hasCredential` cannot answer this: it is ORed across the open doors, so an
   * account holding both reads `true` on the strength of the one that IS set.
   * Both forms below would then demand a current password that does not exist —
   * a field certain to 401, with every attempt spending one count of the §11A
   * lockout budget that protects the door the person really uses.
   *
   * Falling back to `open` when the field is absent keeps an older API behaving
   * exactly as it did; the server always sends it.
   */
  const doors: readonly UserSignInMethod[] = me.doorsWithCredential ?? open

  const [chosen, setChosen] = useState<UserSignInMethod | null>(null)

  /*
   * THE SERVER'S OWN PRIMARY IS THE DEFAULT, not the first door in the list.
   * `doors` arrives in `LOGIN_CREDENTIAL_PATHS` order — pin first, always — so
   * an account holding both used to open on the PIN even when it signs in with
   * an email and a password. `signInWith` is `primaryDoorFor`'s answer, which
   * exists precisely as "the ONE door to show a human", and opening on the door
   * somebody does not use invites them to type the other secret into it.
   */
  const preferred = doors.includes(me.signInWith) ? me.signInWith : doors[0]
  const door = chosen !== null && doors.includes(chosen) ? chosen : preferred

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <Label className="text-muted-foreground text-xs">{t("pref.accountSection")}</Label>
        <p className="text-muted-foreground text-xs">{t("pref.accountSectionHelp")}</p>
      </div>

      <NameForm me={me} />

      {door === undefined ? (
        /* No door this account could prove: both closed (the state a
         * hand-written INSERT produces) or open with no secret set. Either way
         * there is genuinely nothing here to change, and only an administrator
         * can end it. Say so; do not draw a disabled form the person keeps
         * tapping. */
        <Alert>
          <AlertDescription>{t("pref.noDoor")}</AlertDescription>
        </Alert>
      ) : (
        <>
          {/* Asked ONLY when the account really holds both. One open door is not
            * a choice, and a select with one option is a control that teaches
            * nothing. */}
          {doors.length > 1 ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="profileDoor">{t("pref.doorLabel")}</Label>
              <NativeSelect
                id="profileDoor"
                className="w-full"
                value={door}
                onChange={(event) => {
                  setChosen(event.target.value as UserSignInMethod)
                }}
              >
                {doors.map((option) => (
                  <NativeSelectOption key={option} value={option}>
                    {t(option === "pin" ? "pref.doorPin" : "pref.doorPassword")}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </div>
          ) : null}

          {/*
            * `key={door}` IS RULE 5 ACROSS THE SELECTOR, and it is the reason
            * these two are keyed at all. Without it React keeps the same
            * instance when the door changes and only the LABELS change: a PIN
            * typed into "Current PIN" is still in state when the reader switches
            * to the password door, and the next submit sends it as
            * `currentPassword` — the real PIN leaving as a password guess, one
            * bcrypt comparison and one count against the lockout each time,
            * until the account freezes on the door that was working. Remounting
            * discards both fields and the previous door's error and success
            * banners with them.
            *
            * PREFIXED, because these two are SIBLINGS: a bare `key={door}` puts
            * the same key on both children of this fragment, which React treats
            * as a duplicate — it warns and is free to omit or duplicate one of
            * them, and it did.
            */}
          <Separator />
          <SecretForm key={`secret-${door}`} door={door} />
          <Separator />
          <IdentifierForm key={`identifier-${door}`} door={door} />
        </>
      )}
    </section>
  )
}

/* -------------------------------------------------------------------------- */
/* 1. My display name                                                          */
/* -------------------------------------------------------------------------- */

/**
 * NO CURRENT SECRET, and the asymmetry with the two forms below is the point.
 * A display name is what the audit trail calls you, not something you sign in
 * with: typed wrong it is reversible by the person who typed it. An identifier
 * or a secret typed wrong is a lockout, and only those two ask.
 */
function NameForm({ me }: { me: UserResponse }): React.JSX.Element {
  const { t } = useTranslation()
  const write = useProfileWrite()
  const [displayName, setDisplayName] = useState(me.displayName)
  const refusal = attributeProfileError(write.error, "currentPassword", "password")

  return (
    <form
      className="flex flex-col gap-1.5"
      onSubmit={(event) => {
        event.preventDefault()
        write.submit(async () =>
          api.put<ProfileResponse>(endpoints.profile, { displayName: displayName.trim() }),
        )
      }}
    >
      <Label htmlFor="profileName">{t("pref.nameLabel")}</Label>
      <Input
        id="profileName"
        value={displayName}
        autoComplete="name"
        onChange={(event) => {
          setDisplayName(event.target.value)
          write.reset()
        }}
      />
      <p className="text-muted-foreground text-xs">{t("pref.nameHelp")}</p>

      <div className="flex justify-end">
        <Button type="submit" disabled={write.pending || displayName.trim() === ""}>
          {write.pending ? <Loader2Icon className="animate-spin" /> : null}
          {t("pref.nameSubmit")}
        </Button>
      </div>

      <Answer
        refusal={refusal}
        warnings={write.warnings}
        /* The server's row, not the typed value: a rename that was refused for
         * a reason we did not anticipate must not read as having worked. */
        success={write.done ? t("pref.nameSaved", { name: me.displayName }) : null}
      />
    </form>
  )
}

/* -------------------------------------------------------------------------- */
/* 2. My secret                                                                */
/* -------------------------------------------------------------------------- */

/**
 * THE CURRENT SECRET AND THE NEW ONE, IN ONE FORM AND ONE REQUEST.
 *
 * THE PROOF IS ALWAYS THE SAME DOOR AS THE TARGET, which is how rule 3 is kept
 * without this component knowing the rule: proving a PIN to change a PIN and
 * proving a password to change a password are both legal, and the illegal
 * crossing — a PIN rewriting a password — is not expressible on this form.
 */
function SecretForm({ door }: { door: UserSignInMethod }): React.JSX.Element {
  const { t } = useTranslation()
  const write = useProfileWrite()
  const [current, setCurrent] = useState("")
  const [next, setNext] = useState("")

  const currentField = door === "pin" ? "currentPin" : "currentPassword"
  const nextField = door === "pin" ? "pin" : "password"
  const currentLabel = t(door === "pin" ? "pref.currentPin" : "pref.currentPassword")
  const nextLabel = t(door === "pin" ? "pref.newPin" : "pref.newPassword")
  const refusal = attributeProfileError(write.error, currentField, nextField)

  return (
    <form
      className="flex flex-col gap-3"
      /*
       * NAMED, SO THE TWO FORMS ARE TELLABLE APART. Both of them ask for the
       * current secret, so "Current PIN" appears twice on the sheet — for a
       * screen reader that is two identically-labelled inputs unless each form
       * is a landmark with a name of its own. It is also what lets a test say
       * WHICH "Current PIN" it means, rather than indexing into a list and
       * silently following the DOM order around.
       */
      aria-labelledby="profileSecretTitle"
      onSubmit={(event) => {
        event.preventDefault()
        const body =
          door === "pin" ? { currentPin: current, pin: next } : { currentPassword: current, password: next }

        /*
         * `reauthenticating: true` IS LOAD-BEARING. Without it a wrong current
         * secret is a 401 that `lib/api.ts` reads as an expired session: it
         * refreshes, replays the request — spending a second bcrypt comparison
         * and a second count against the lockout — and then signs the owner out
         * of the app for a mistyped digit.
         */
        write.submit(async () =>
          api.post<ProfileResponse>(endpoints.profileCredential, body, {
            reauthenticating: true,
          }),
        )

        // Rule 5. The secret has left in the body; nothing keeps a copy, and it
        // is cleared before the answer even arrives.
        setCurrent("")
        setNext("")
      }}
    >
      <Label id="profileSecretTitle" className="text-muted-foreground text-xs">
        {t("pref.secretTitle")}
      </Label>

      <Secret
        id="profileCurrentSecret"
        label={currentLabel}
        help={t("pref.currentHelp")}
        door={door}
        autoComplete="current-password"
        value={current}
        error={refusal.current}
        onChange={(value) => {
          setCurrent(value)
          write.reset()
        }}
      />

      <Secret
        id="profileNewSecret"
        label={nextLabel}
        help={t(door === "pin" ? "pref.newPinHelp" : "pref.newPasswordHelp")}
        door={door}
        autoComplete="new-password"
        value={next}
        error={refusal.next}
        onChange={(value) => {
          setNext(value)
          write.reset()
        }}
      />

      <div className="flex justify-end">
        <Button type="submit" disabled={write.pending || current === "" || next === ""}>
          {write.pending ? <Loader2Icon className="animate-spin" /> : null}
          {t(door === "pin" ? "pref.secretSubmitPin" : "pref.secretSubmitPassword")}
        </Button>
      </div>

      <Answer
        refusal={refusal}
        warnings={write.warnings}
        success={
          write.done
            ? t(door === "pin" ? "pref.secretSavedPin" : "pref.secretSavedPassword")
            : null
        }
      />
    </form>
  )
}

/* -------------------------------------------------------------------------- */
/* 3. What I type to sign in                                                   */
/* -------------------------------------------------------------------------- */

/**
 * THE IDENTIFIER IS HALF A CREDENTIAL, WHICH IS WHY THIS FORM ASKS TOO. A
 * mistyped email does not bounce a message — it locks the person out on their
 * next sign-in, and they cannot come back here to fix it, because reaching this
 * sheet requires signing in. The server's success `warnings` say in plain words
 * what to type from now on, and {@link Answer} shows them verbatim.
 */
function IdentifierForm({ door }: { door: UserSignInMethod }): React.JSX.Element {
  const { t } = useTranslation()
  const write = useProfileWrite()
  const [current, setCurrent] = useState("")
  const [identifier, setIdentifier] = useState("")

  const currentField = door === "pin" ? "currentPin" : "currentPassword"
  const nextField = door === "pin" ? "phone" : "email"
  const currentLabel = t(door === "pin" ? "pref.currentPin" : "pref.currentPassword")
  const refusal = attributeProfileError(write.error, currentField, nextField)
  const errorId = "profileIdentifier-error"

  return (
    <form
      className="flex flex-col gap-3"
      /* See the note on the secret form — both ask for the current secret. */
      aria-labelledby="profileIdentifierTitle"
      onSubmit={(event) => {
        event.preventDefault()
        const body =
          door === "pin"
            ? { currentPin: current, phone: identifier.trim() }
            : { currentPassword: current, email: identifier.trim() }

        write.submit(async () =>
          api.post<ProfileResponse>(endpoints.profileIdentifier, body, {
            reauthenticating: true,
          }),
        )
        setCurrent("")
      }}
    >
      <Label id="profileIdentifierTitle" className="text-muted-foreground text-xs">
        {t("pref.identifierTitle")}
      </Label>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="profileIdentifier">
          {t(door === "pin" ? "pref.newPhone" : "pref.newEmail")}
        </Label>
        <Input
          id="profileIdentifier"
          inputMode={door === "pin" ? "tel" : "email"}
          autoComplete={door === "pin" ? "tel" : "email"}
          value={identifier}
          aria-invalid={refusal.next !== null}
          aria-describedby={refusal.next === null ? undefined : errorId}
          onChange={(event) => {
            setIdentifier(event.target.value)
            write.reset()
          }}
        />
        {refusal.next === null ? (
          <p className="text-muted-foreground text-xs">
            {t(door === "pin" ? "pref.identifierHelpPin" : "pref.identifierHelpPassword")}
          </p>
        ) : (
          <p id={errorId} role="alert" className="text-destructive text-xs">
            {t("pref.fieldRefused", {
              field: t(door === "pin" ? "pref.newPhone" : "pref.newEmail"),
              message: refusal.next,
            })}
          </p>
        )}
      </div>

      <Secret
        id="profileIdentifierCurrent"
        label={currentLabel}
        help={t("pref.currentHelp")}
        door={door}
        autoComplete="current-password"
        value={current}
        error={refusal.current}
        onChange={(value) => {
          setCurrent(value)
          write.reset()
        }}
      />

      <div className="flex justify-end">
        <Button type="submit" disabled={write.pending || current === "" || identifier.trim() === ""}>
          {write.pending ? <Loader2Icon className="animate-spin" /> : null}
          {t(door === "pin" ? "pref.identifierSubmitPin" : "pref.identifierSubmitPassword")}
        </Button>
      </div>

      <Answer refusal={refusal} warnings={write.warnings} success={write.done ? t("status.saved") : null} />
    </form>
  )
}

/* -------------------------------------------------------------------------- */
/* Shared parts                                                                */
/* -------------------------------------------------------------------------- */

/**
 * A secret input, and the message that belongs to THIS field when it is refused.
 *
 * `type="password"` ON A PIN TOO: six digits typed on a shared office desktop
 * are as worth covering as a password. The value is never read back, never
 * echoed, and the field is cleared on submit.
 *
 * THE ERROR IS RENDERED HERE, AGAINST THE INPUT, and it repeats the field's own
 * label. The server's 401 is deliberately generic ("That is not your current PIN
 * or password"); at the foot of a form that also contains "New PIN" it reads as
 * the NEW value being rejected. Naming the field in the message, tying it with
 * `aria-describedby`, and leaving the other input untouched is what makes the
 * person retype the right half.
 */
function Secret({
  id,
  label,
  help,
  door,
  autoComplete,
  value,
  error,
  onChange,
}: {
  id: string
  label: string
  help: string
  door: UserSignInMethod
  autoComplete: string
  value: string
  error: string | null
  onChange: (value: string) => void
}): React.JSX.Element {
  const { t } = useTranslation()
  const errorId = `${id}-error`

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="password"
        autoComplete={autoComplete}
        inputMode={door === "pin" ? "numeric" : "text"}
        value={value}
        aria-invalid={error !== null}
        aria-describedby={error === null ? undefined : errorId}
        onChange={(event) => {
          onChange(event.target.value)
        }}
      />
      {error === null ? (
        <p className="text-muted-foreground text-xs">{help}</p>
      ) : (
        <p id={errorId} role="alert" className="text-destructive text-xs">
          {t("pref.fieldRefused", { field: label, message: error })}
        </p>
      )}
    </div>
  )
}

/**
 * What the server said, unedited — a refusal that belongs to no field, the
 * warnings, or the confirmation.
 *
 * IT TRANSLATES NOTHING IT DID NOT WRITE. "From now on sign in with the email
 * address …" and "Turning on a second sign-in method is an administrator's
 * decision" are sentences written for the person reading this sheet, and each
 * names what to do next. A friendlier local rewording drops that half.
 */
function Answer({
  refusal,
  warnings,
  success,
}: {
  refusal: ProfileRefusal
  warnings: readonly string[]
  success: string | null
}): React.JSX.Element | null {
  const { t } = useTranslation()

  if (refusal.generic || refusal.form !== null) {
    return (
      <Alert variant="destructive">
        <AlertTitle>{t("err.couldNotSave")}</AlertTitle>
        <AlertDescription>
          {refusal.form ?? t("err.somethingWentWrong")}
        </AlertDescription>
      </Alert>
    )
  }

  // A field-level refusal is already shown against its input; a banner repeating
  // it would say the same thing twice and pull the eye away from the field.
  if (refusal.current !== null || refusal.next !== null) return null

  if (success === null) return null

  return (
    <Alert>
      <AlertTitle>{success}</AlertTitle>
      {warnings.length === 0 ? null : (
        <AlertDescription className="flex flex-col gap-1">
          {warnings.map((warning) => (
            <span key={warning}>{warning}</span>
          ))}
        </AlertDescription>
      )}
    </Alert>
  )
}
